/**
 * GET /me/notifications/delivery-health — whether the viewer has any
 * notification channel configured (verified email or linked Slack account).
 * Shares its query key with `NotificationsSection` (org/personal settings
 * page) so a prefetch here reuses that page's cache and vice versa.
 *
 * @module features/notifications/hooks/useNotificationDeliveryHealth
 */
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../config';

export type NotificationDeliveryHealth = {
  primaryEmail: string | null;
  emailVerified: boolean;
  slackConnected: boolean;
  slackLinked: boolean;
  slackTeamName?: string | null;
};

export const NOTIFICATION_DELIVERY_HEALTH_QUERY_KEY = ['me-notification-health'] as const;

export function useNotificationDeliveryHealth(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: NOTIFICATION_DELIVERY_HEALTH_QUERY_KEY,
    enabled: opts?.enabled ?? true,
    queryFn: async (): Promise<{ ok: boolean; data: NotificationDeliveryHealth }> => {
      const res = await fetch(`${API_URL}/me/notifications/delivery-health`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load delivery health');
      return res.json();
    },
  });
}

/** True once we positively know the viewer has no configured delivery channel. */
export function hasNoDeliveryChannel(health: NotificationDeliveryHealth | undefined): boolean {
  if (!health) return false;
  return !health.emailVerified && !health.slackLinked;
}
